import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getVehicles, getServiceRecords, getAppointments, getCollectionValue, getBatchVehicleScoreCounts } from '../../lib/supabase'
import SignUpPrompt from '../../components/SignUpPrompt'
import OnboardingWizard from '../../components/OnboardingWizard'
import { calculateVehicleScore, calculateCollectionScore, getScoreTier, getTierProgress } from '../../lib/garageScore'

import { format, differenceInDays } from 'date-fns'
import {
  Car, ChevronRight, Plus, ArrowRight, Bell, TrendingUp,
  Calendar, Wrench, Star,
  DollarSign, AlertTriangle, AlertCircle, Clock, Headphones
} from 'lucide-react'

// Get time-based greeting
const getGreeting = () => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

// Format relative time for activity feed (e.g., "2h ago", "3d ago")
const formatRelativeTime = (dateString) => {
  if (!dateString) return 'recently'

  const date = new Date(dateString)
  const now = new Date()
  const seconds = Math.floor((now - date) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.floor(days / 30)
  return `${months}mo ago`
}

// Generate activity feed from user's vehicles and service records
const generateActivityFeed = (vehicles, serviceRecords, profile) => {
  const activities = []

  // Recently added vehicles (up to 3)
  vehicles.slice(0, 3).forEach(v => {
    activities.push({
      id: `v-${v.id}`,
      type: 'vehicle_added',
      user: {
        name: profile?.full_name?.split(' ')[0] || 'You',
        avatar: (profile?.full_name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()
      },
      content: `added a ${v.year} ${v.make} ${v.model}`,
      timestamp: formatRelativeTime(v.created_at),
      image: v.primary_image_url
    })
  })

  // Recent service records (up to 3)
  serviceRecords.slice(0, 3).forEach(r => {
    const v = vehicles.find(vehicle => vehicle.id === r.vehicle_id)
    activities.push({
      id: `s-${r.id}`,
      type: 'service',
      user: {
        name: profile?.full_name?.split(' ')[0] || 'You',
        avatar: (profile?.full_name || 'U').split(' ').map(n => n[0]).join('').toUpperCase()
      },
      content: `logged ${r.service_type} on ${v?.year || ''} ${v?.make || ''} ${v?.model || ''}`.trim(),
      timestamp: formatRelativeTime(r.service_date || r.created_at)
    })
  })

  // Sort by most recent (assuming newer created_at means more recent) and take top 5
  return activities.sort((a, b) => {
    // For demo purposes, we'll just return the activities as-is
    // In production, you might want to sort by an actual timestamp
    return activities.slice(0, 5)
  }).slice(0, 5)
}

// Stat card component
const StatCard = ({ icon: Icon, label, value, subtext, color = 'orange', link }) => {
  const colorClasses = {
    orange: 'bg-rdc-orange/10 text-rdc-orange',
    green: 'bg-green-100 text-green-600',
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    purple: 'bg-purple-100 text-purple-600'
  }

  const content = (
    <div className="card p-5 hover:shadow-md transition-shadow h-full">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-rdc-taupe mb-1">{label}</p>
          <p className="text-2xl font-semibold text-black">{value}</p>
          {subtext && <p className="text-xs text-rdc-taupe mt-1">{subtext}</p>}
        </div>
        <div className={`p-2.5 rounded-full ${colorClasses[color]}`}>
          <Icon size={20} />
        </div>
      </div>
    </div>
  )

  if (link) {
    return <Link to={link}>{content}</Link>
  }
  return content
}

// Activity feed item
const ActivityItem = ({ activity }) => (
  <div className="flex items-start gap-3 py-3 border-b border-rdc-stone/20 last:border-0">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rdc-stone to-rdc-taupe flex items-center justify-center flex-shrink-0">
      <span className="text-[10px] font-semibold text-white">{activity.user.avatar}</span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm text-rdc-charcoal">
        <span className="font-medium text-black">{activity.user.name}</span>{' '}
        {activity.content}
      </p>
      <p className="text-xs text-rdc-taupe mt-0.5">{activity.timestamp}</p>
    </div>
  </div>
)

// Mini vehicle card with garage score tier badge
const MiniVehicleCard = ({ vehicle, isDemo, onDemoClick, scoreData }) => {
  const tier = scoreData?.tierInfo

  const content = (
    <>
      <div className="aspect-[4/3] rounded-lg overflow-hidden bg-rdc-cream border border-rdc-stone/30 group-hover:border-rdc-orange/40 transition-colors relative">
        {vehicle.primary_image_url ? (
          <img
            src={vehicle.primary_image_url}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Car size={24} className="text-rdc-stone" strokeWidth={1.25} />
          </div>
        )}
        {isDemo && (
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-medium">
              Sign up to add yours
            </span>
          </div>
        )}
      </div>
      <div className="mt-2">
        <div className="text-xs text-rdc-taupe">{vehicle.year}</div>
        <h4 className="text-sm font-medium text-black truncate group-hover:text-rdc-orange transition-colors">
          {vehicle.make} {vehicle.model}
        </h4>
        {tier && !isDemo && (
          <div className="mt-1.5 flex items-center gap-1">
            <div className={`w-2 h-2 rounded-full ${tier.bgClass}`} />
            <span className="text-xs text-rdc-taupe">{tier.name}</span>
          </div>
        )}
      </div>
    </>
  )

  if (isDemo) {
    return (
      <button onClick={onDemoClick} className="flex-shrink-0 w-44 group text-left">
        {content}
      </button>
    )
  }

  return (
    <Link to={`/garage/${vehicle.id}`} className="flex-shrink-0 w-44 group">
      {content}
    </Link>
  )
}

// Alert/Reminder item
const AlertItem = ({ alert }) => {
  const getUrgencyColor = () => {
    if (alert.daysUntil < 0) return 'bg-red-50 border-red-200 text-red-700'
    if (alert.daysUntil <= 7) return 'bg-red-50 border-red-100 text-red-600'
    if (alert.daysUntil <= 30) return 'bg-amber-50 border-amber-100 text-amber-700'
    return 'bg-green-50 border-green-100 text-green-700'
  }

  const getUrgencyText = () => {
    if (alert.daysUntil < 0) return `${Math.abs(alert.daysUntil)}d overdue`
    if (alert.daysUntil === 0) return 'Due today'
    return `${alert.daysUntil}d left`
  }

  return (
    <div className={`p-3 rounded-lg border ${getUrgencyColor()}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{alert.title}</p>
          <p className="text-xs opacity-75 truncate">{alert.subtitle}</p>
        </div>
        <span className="text-xs font-medium flex items-center gap-1 flex-shrink-0 ml-2">
          <Clock size={12} />
          {getUrgencyText()}
        </span>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const isGuest = !user

  const [vehicles, setVehicles] = useState([])
  const [serviceRecords, setServiceRecords] = useState([])
  const [appointments, setAppointments] = useState([])
  const [collectionValueData, setCollectionValueData] = useState(null)
  const [vehicleScores, setVehicleScores] = useState([])
  const [collectionScore, setCollectionScore] = useState(null)
  const [loading, setLoading] = useState(!isGuest)
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)

  const greeting = getGreeting()
  const firstName = isGuest ? 'Guest' : (profile?.full_name?.split(' ')[0] || 'there')

  useEffect(() => {
    if (isGuest) {
      setVehicles([])
      setLoading(false)
    } else {
      loadData()
    }
  }, [user, isGuest])

  // Show onboarding if user has no vehicles and hasn't dismissed it
  useEffect(() => {
    if (!isGuest && !loading && vehicles.length === 0) {
      const isDismissed = localStorage.getItem('rdc_onboarding_dismissed')
      setShowOnboarding(!isDismissed)
    }
  }, [isGuest, loading, vehicles.length])

  const loadData = async () => {
    if (!user) {
      setLoading(false)
      return
    }
    try {
      const [vehiclesRes, servicesRes, appointmentsRes, valueRes] = await Promise.all([
        getVehicles(user.id),
        getServiceRecords(),
        getAppointments(user.id),
        getCollectionValue(user.id)
      ])

      const userVehicles = vehiclesRes.data || []
      setVehicles(userVehicles)

      const vehicleIds = userVehicles.map(v => v.id)
      const userServices = (servicesRes.data || []).filter(s => vehicleIds.includes(s.vehicle_id))
      setServiceRecords(userServices)

      setAppointments(appointmentsRes.data || [])
      setCollectionValueData(valueRes.data)

      // Calculate garage scores with accurate counts from DB
      let scores = []
      if (vehicleIds.length > 0) {
        try {
          const counts = await getBatchVehicleScoreCounts(vehicleIds)
          // Add photo counts from primary_image_url
          userVehicles.forEach(v => {
            if (!counts[v.id]) counts[v.id] = { services: 0, docs: 0, vals: 0 }
            counts[v.id].photos = v.primary_image_url ? 1 : 0
          })
          scores = userVehicles.map(vehicle => {
            const c = counts[vehicle.id] || { services: 0, docs: 0, vals: 0, photos: 0 }
            return calculateVehicleScore(vehicle, c.services, c.docs, c.vals, c.photos)
          })
        } catch (scoreErr) {
          console.error('Error calculating scores:', scoreErr)
          // Fallback to basic scoring
          scores = userVehicles.map(vehicle => {
            const serviceCount = userServices.filter(s => s.vehicle_id === vehicle.id).length
            return calculateVehicleScore(vehicle, serviceCount, 0, 0, 0)
          })
        }
      }

      setVehicleScores(scores)

      // Calculate collection-level score
      if (scores.length > 0) {
        const collScore = calculateCollectionScore(scores)
        setCollectionScore(collScore)
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddVehicle = () => {
    if (isGuest) {
      setShowSignUpPrompt(true)
    } else {
      navigate('/garage/add')
    }
  }

  // Calculate statistics
  const totalVehicles = vehicles.length
  const totalMiles = vehicles.reduce((sum, v) => sum + (v.mileage || 0), 0)
  const totalServiceCost = serviceRecords.reduce((sum, r) => sum + (r.total_cost || 0), 0)
  const collectionValue = collectionValueData?.total || 0
  const readyCount = vehicles.filter(v => v.status === 'ready').length

  // Format collection value for display
  const formatCollectionValue = (value) => {
    if (!value) return '$0'
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M'
    } else if (value >= 1000) {
      return '$' + (value / 1000).toFixed(0) + 'K'
    }
    return '$' + value.toLocaleString()
  }

  // Get garage score tier info
  const garageScoreTier = collectionScore?.tierInfo || null
  const garageScoreDisplay = collectionScore ? collectionScore.averageScore : 0
  const tierProgress = getTierProgress(garageScoreDisplay)
  const provenanceSubtext = tierProgress.nextTier
    ? `${tierProgress.pointsUntilNextTier} pts to ${tierProgress.nextTier.name}`
    : garageScoreTier?.name || 'Add vehicles'

  // Calculate alerts
  const today = new Date()
  const alerts = []

  vehicles.forEach(vehicle => {
    if (vehicle.registration_expiry) {
      const expiry = new Date(vehicle.registration_expiry)
      const daysUntil = differenceInDays(expiry, today)
      if (daysUntil <= 60) {
        alerts.push({
          id: `reg-${vehicle.id}`,
          title: 'Registration Expiring',
          subtitle: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          daysUntil,
          type: 'registration'
        })
      }
    }

    if (vehicle.insurance_expiry) {
      const expiry = new Date(vehicle.insurance_expiry)
      const daysUntil = differenceInDays(expiry, today)
      if (daysUntil <= 60) {
        alerts.push({
          id: `ins-${vehicle.id}`,
          title: 'Insurance Expiring',
          subtitle: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
          daysUntil,
          type: 'insurance'
        })
      }
    }
  })

  alerts.sort((a, b) => a.daysUntil - b.daysUntil)

  // Get upcoming appointments
  const upcomingAppointments = appointments
    .filter(a => new Date(a.scheduled_date) >= today && a.status !== 'cancelled')
    .slice(0, 2)

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Welcome Header */}
      <div className="mb-8">
        <p className="text-label mb-1">{greeting}</p>
        <h1 className="page-title">Welcome back, {firstName}</h1>
        <p className="text-body text-rdc-charcoal mt-1">
          Your collection at a glance
        </p>
      </div>

      {/* Main Dashboard Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Left Column - Main Content (2 cols) */}
        <div className="lg:col-span-2 space-y-6">

          {/* Collection Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatCard
              icon={Car}
              label="Vehicles"
              value={totalVehicles}
              subtext={readyCount > 0 ? `${readyCount} road ready` : 'in your garage'}
              color="orange"
              link="/garage"
            />
            <StatCard
              icon={DollarSign}
              label="Collection Value"
              value={formatCollectionValue(collectionValue)}
              subtext={collectionValue > 0 ? 'estimated total' : 'request valuation'}
              color="green"
              link="/valuations"
            />
            <StatCard
              icon={TrendingUp}
              label="Provenance Score"
              value={garageScoreDisplay}
              subtext={provenanceSubtext}
              color="blue"
              link="/garage"
            />
            <StatCard
              icon={AlertTriangle}
              label="Alerts"
              value={alerts.length}
              subtext={alerts.length === 0 ? "all clear" : "need attention"}
              color={alerts.length > 0 ? 'amber' : 'green'}
              link="/maintenance"
            />
          </div>

          {/* Garage Snapshot */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-label flex items-center gap-2">
                <Car size={16} className="text-rdc-orange" />
                Your Garage
              </h2>
              <Link
                to="/garage"
                className="text-xs text-rdc-orange font-medium hover:underline flex items-center gap-1"
              >
                View All <ArrowRight size={12} />
              </Link>
            </div>

            {vehicles.length === 0 && !isGuest ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-rdc-orange/10 to-rdc-orange/5 flex items-center justify-center">
                  <Car size={32} className="text-rdc-orange" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold text-black mb-2">Welcome to RDC Digital Garage!</h3>
                <p className="text-sm text-rdc-charcoal mb-6">Let's add your first vehicle — it takes 60 seconds.</p>

                {/* Benefit callouts */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs font-medium text-blue-700">Track Maintenance</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs font-medium text-green-700">Document History</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs font-medium text-amber-700">Know Your Value</p>
                  </div>
                </div>

                <button onClick={handleAddVehicle} className="btn-primary inline-flex items-center gap-2 text-sm">
                  <Plus size={16} />
                  Add Your First Vehicle
                </button>
              </div>
            ) : (
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
                {vehicles.slice(0, 4).map((vehicle) => {
                  const vehicleScore = vehicleScores.find(score => {
                    // Find the score index by vehicle order
                    const vehicleIndex = vehicles.indexOf(vehicle)
                    return vehicleIndex >= 0 && vehicleIndex < vehicleScores.length
                  }) || vehicleScores[vehicles.indexOf(vehicle)]

                  return (
                    <MiniVehicleCard
                      key={vehicle.id}
                      vehicle={vehicle}
                      isDemo={isGuest}
                      onDemoClick={() => setShowSignUpPrompt(true)}
                      scoreData={vehicleScore}
                    />
                  )
                })}
                {/* Add Vehicle button for guests */}
                {isGuest && (
                  <button
                    onClick={handleAddVehicle}
                    className="flex-shrink-0 w-44 aspect-[4/3] rounded-lg border-2 border-dashed border-rdc-orange/40 flex flex-col items-center justify-center text-rdc-orange hover:bg-rdc-orange/5 transition-colors"
                  >
                    <Plus size={24} className="mb-1" />
                    <span className="text-xs font-medium">Add Your Vehicle</span>
                  </button>
                )}
                {!isGuest && vehicles.length > 4 && (
                  <Link
                    to="/garage"
                    className="flex-shrink-0 w-44 aspect-[4/3] rounded-lg border-2 border-dashed border-rdc-stone/30 flex flex-col items-center justify-center text-rdc-taupe hover:border-rdc-orange/40 hover:text-rdc-orange transition-colors"
                  >
                    <span className="text-xl font-medium">+{vehicles.length - 4}</span>
                    <span className="text-xs">more</span>
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Link
              to="/maintenance"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-amber-100 flex items-center justify-center">
                <Bell size={18} className="text-amber-600" />
              </div>
              <div className="text-sm font-medium text-black">Maintenance</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Check alerts</p>
            </Link>
            <Link
              to="/valuations"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp size={18} className="text-green-600" />
              </div>
              <div className="text-sm font-medium text-black">Valuations</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Track value</p>
            </Link>
            <Link
              to="/service-history"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
                <Wrench size={18} className="text-blue-600" />
              </div>
              <div className="text-sm font-medium text-black">Service</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Log records</p>
            </Link>
            <Link
              to="/specialists"
              className="card p-4 group hover:border-rdc-orange/30 text-center"
            >
              <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
                <Star size={18} className="text-purple-600" />
              </div>
              <div className="text-sm font-medium text-black">Specialists</div>
              <p className="text-xs text-rdc-taupe mt-0.5">Find experts</p>
            </Link>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">

          {/* Alerts (if any) */}
          {alerts.length > 0 && (
            <div className="card p-5 border-l-4 border-amber-400">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-label flex items-center gap-2 text-amber-700">
                  <AlertCircle size={14} />
                  Alerts
                </h2>
                <Link
                  to="/maintenance"
                  className="text-xs text-amber-600 font-medium hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert) => (
                  <AlertItem key={alert.id} alert={alert} />
                ))}
              </div>
            </div>
          )}

          {/* Concierge CTA — Primary */}
          <Link
            to="/concierge"
            className="card p-5 bg-gradient-to-br from-rdc-charcoal to-rdc-charcoal/90 border-rdc-orange/30 block hover:shadow-lg transition-shadow group"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-11 h-11 rounded-full bg-rdc-orange/20 flex items-center justify-center group-hover:bg-rdc-orange/30 transition-colors">
                <Headphones size={22} className="text-rdc-orange" />
              </div>
              <div>
                <h3 className="font-semibold text-white">RDC Concierge</h3>
                <p className="text-xs text-rdc-stone">We handle everything for your collection</p>
              </div>
            </div>
            <p className="text-sm text-rdc-stone/80 mb-3">
              Transport, service, storage, restoration — tell us what you need and we'll take care of it.
            </p>
            <div className="text-sm text-rdc-orange font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
              Start a Request
              <ArrowRight size={14} />
            </div>
          </Link>

          {/* Recent Activity */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-label flex items-center gap-2">
                <Clock size={14} className="text-rdc-orange" />
                Recent Activity
              </h2>
            </div>
            <div className="divide-y divide-rdc-stone/20">
              {vehicles.length > 0 || serviceRecords.length > 0 ? (
                generateActivityFeed(vehicles, serviceRecords, profile).map((activity) => (
                  <ActivityItem key={activity.id} activity={activity} />
                ))
              ) : (
                <div className="text-center py-6">
                  <p className="text-sm text-rdc-taupe">Your activities will appear here</p>
                </div>
              )}
            </div>
          </div>

          {/* Upcoming Appointments */}
          {upcomingAppointments.length > 0 && (
            <div className="card p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-label flex items-center gap-2">
                  <Calendar size={14} className="text-rdc-orange" />
                  Appointments
                </h2>
                <Link
                  to="/appointments"
                  className="text-xs text-rdc-orange font-medium hover:underline"
                >
                  View all
                </Link>
              </div>
              <div className="space-y-2">
                {upcomingAppointments.map(apt => (
                  <div key={apt.id} className="p-3 bg-rdc-cream/50 rounded-lg">
                    <p className="font-medium text-sm text-black truncate">{apt.title}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-rdc-taupe">
                        {format(new Date(apt.scheduled_date), 'MMM d')}
                        {apt.scheduled_time && ` • ${apt.scheduled_time}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Sign Up Prompt Modal */}
      <SignUpPrompt
        isOpen={showSignUpPrompt}
        onClose={() => setShowSignUpPrompt(false)}
        action="add your vehicles"
      />

      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboarding}
        onComplete={(vehicleId) => {
          setShowOnboarding(false)
          loadData() // Refresh the vehicle list
        }}
        onSkip={() => {
          setShowOnboarding(false)
        }}
      />
    </div>
  )
}
